/**
 * Analytics Integration - GTM, GA4, Meta Pixel, LinkedIn
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    fbq?: (...args: any[]) => void;
    _linkedin_data_partner_ids?: string[];
    _hsq?: any[];
  }
}

/**
 * Initialize Google Tag Manager
 */
export function initGTM(gtmId: string): void {
  if (typeof window === 'undefined' || !gtmId) return;
  
  // GTM Script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);
  
  // GTM NoScript
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/**
 * Track page view (via GTM dataLayer)
 */
export function trackPageView(url: string): void {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: url,
    page_title: document.title,
  });
}

/**
 * Track custom event
 */
export function trackEvent(
  eventName: string,
  parameters?: Record<string, any>
): void {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...parameters,
  });
}

/**
 * Initialize HubSpot Tracking (EU Server - Direct)
 * Loads HubSpot directly from EU server for GDPR compliance
 * Note: This is third-party but necessary for lead tracking
 */
export function initHubSpotTracking(portalId: string): void {
  if (typeof window === 'undefined' || !portalId) return;
  
  // Skip if already loaded
  if (document.getElementById('hs-script-loader')) return;
  
  // Initialize HubSpot queue
  window._hsq = window._hsq || [];
  
  // Load HubSpot tracking script directly from EU server
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `https://js-eu1.hs-scripts.com/${portalId}.js`;
  
  // Add to body for better performance
  document.body.appendChild(script);
}

/**
 * Initialize analytics based on consent
 * GTM loads always (cookieless), GA4/Pixels managed via GTM tags
 */
export function initializeAnalytics(consent: {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}): void {
  const hubspotPortalId = '26049307'; // HubSpot Portal ID
  
  // GTM already loaded in layout.tsx - just update consent
  // GTM tags will handle GA4, Meta, LinkedIn based on consent triggers
  
  // HubSpot still loads directly (needed for form integration)
  if (consent.analytics) {
    initHubSpotTracking(hubspotPortalId);
  }
}

/**
 * Track conversion event (form submission)
 * Supports Enhanced Conversions (cookieless) for Google Ads
 */
export function trackConversion(
  conversionLabel?: string,
  enhancedConversionData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    address?: {
      country?: string;
      postal_code?: string;
    };
  }
): void {
  if (typeof window === 'undefined') return;
  
  // Push to dataLayer for GTM (always tracked - cookieless)
  trackEvent('conversion', {
    conversion_label: conversionLabel || 'demo_request',
    conversion_type: 'lead',
    // Enhanced Conversion Data (hashed by GTM)
    ...(enhancedConversionData && {
      email: enhancedConversionData.email,
      phone_number: enhancedConversionData.phone,
      first_name: enhancedConversionData.firstName,
      last_name: enhancedConversionData.lastName,
      address: enhancedConversionData.address,
    }),
  });
  
  // GA4 conversion event (only if analytics consent and gtag is loaded via GTM)
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: process.env.NEXT_PUBLIC_GA4_ID,
      event_category: 'lead',
      event_label: conversionLabel || 'demo_request',
    });
  }
  
  // Meta Pixel Lead event (only if marketing consent and fbq is loaded via GTM)
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'Demo Request',
      content_category: 'B2B Lead',
    });
  }
}
